import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { Company, emptyCompany } from 'src/app/models/data/company';
import { emptyRole, Role } from 'src/app/models/data/role';
import {
	SystemCapabilityEnum,
	SystemCapabilityItem,
} from 'src/app/models/data/systemCapability';
import { Mode } from 'src/app/models/model';
import { MatDivider } from '@angular/material/list';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { MatInput } from '@angular/material/input';
import { SystemCapabilityTableComponent } from '../../ui/roles/components/system-capability-table/system-capability-table.component';

@Component({
	selector: 'app-assign-role',
	templateUrl: './assign-role.component.html',
	styleUrls: [
		'./assign-role.component.scss',
		'../../ui/common/main.scss',
		'../../ui/common/stepper.scss',
	],
	imports: [
		MatDivider,
		MatFormField,
		MatLabel,
		MatSelect,
		FormsModule,
		MatOption,
		LoadingIndicatorComponent,
		MatInput,
		SystemCapabilityTableComponent,
	],
})
export class AssignRoleComponent implements OnInit {
	@Input() showCompanyDropdown = true;
	@Input() companyId?: string;
	@Output() selectionChangedEvents =
		new EventEmitter<SelectedCompanyRoleEvent>();

	companies: Company[] = [];
	roles: (Role | string)[] = [];
	systemCapabilityItems: SystemCapabilityItem[] = [];

	selectedCompany?: Company;
	selectedRole?: Role;
	addCustomRoleSelected = false;
	customRoleName = '';
	companyOptions: { value: any; label: string }[] = [];
	roleOptions: { value: any; label: string }[] = [];

	systemCapabilityTableMode: Mode = Mode.viewOnly;
	sentrilockCompanyId?: string;

	companyType: any = 'advanced';

	apiLoadingKeys = ApiLoadingKeys;

	get roleName(): string {
		return this.selectedRole?.name ?? 'Custom';
	}

	get capabilityToggleDisabled(): boolean {
		return !this.addCustomRoleSelected;
	}

	constructor(
		private readonly apiService: ApiService,
		private readonly scService: SystemCapabilityService
	) {}

	async ngOnInit(): Promise<void> {
		this.sentrilockCompanyId = await this.apiService.getSentrilockCompanyId();
		if (this.companyId !== undefined) {
			const response = await this.apiService.getCompanyById(this.companyId);
			this.companies = [response.company];
		} else {
			const userProfile = await this.apiService.getUserProfile();
			const activeCompany: Company | undefined =
				userProfile.activeRoleDetails?.company;

			// Only someone with GlobalCompanyManagement can add users to another company
			let companies: Company[] = [];
			if (!this.scService.has(SystemCapabilityEnum.globalCompanyManagement)) {
				companies = activeCompany ? [activeCompany] : [];
			} else {
				const queryParameters: QueryParam[] = [
					{ param: 'rowIndex', value: 0 },
					{ param: 'rowCount', value: 100 },
				];
				companies = await this.apiService
					.getCompanyRelationships(queryParameters)
					.then((relationships) => {
						const values = relationships.map((it) => it.company);
						return activeCompany === undefined
							? values
							: [activeCompany, ...values];
					});
			}

			this.companies = [emptyCompany(), ...companies];
		}

		this.companyOptions = this.companies.map((it) => ({
			value: it,
			label: it.name,
		}));
		await this.onCompanySelected(this.companies[0]);
	}

	async onCompanySelected(selectedCompany: Company): Promise<void> {
		this.selectedCompany = selectedCompany;
		this.selectedRole = undefined;
		this.systemCapabilityItems = [];

		this.selectionChangedEvents.emit({
			company: this.selectedCompany,
			role: this.selectedRole,
		});

		const companyId = this.selectedCompany.id;
		if (companyId.length !== 0) {
			// Find the company type
			this.companyType =
				(await this.apiService.getCompanyById(companyId)).company.companyType ??
				'advanced';

			const queryParameters: QueryParam[] = [
				{ param: 'rowIndex', value: 0 },
				{ param: 'rowCount', value: 100 },
			];

			this.roles = await this.apiService
				.getCompanyRolesByCompanyId(this.selectedCompany.id, queryParameters)
				.then((results) => [
					emptyRole(),
					addCustomRoleOption,
					...results.map((it) => it.role),
				]);

			const userIsOracle = this.scService.isOracle;
			const userIsCompanyAdmin = this.scService.isCompanyAdmin;

			// Remove Oracle if user isn't Oracle or company isn't Sentrilock
			if (!userIsOracle || companyId !== this.sentrilockCompanyId) {
				this.roles = this.roles.filter(
					(role) => typeof role === 'string' || !(role as Role).isOracle
				);
			}

			// Remove Company Admin if user isn't Company Admin or Oracle
			if (!userIsCompanyAdmin && !userIsOracle) {
				this.roles = this.roles.filter(
					(role) => typeof role === 'string' || !(role as Role).isCompanyAdmin
				);
			}

			this.roleOptions = this.roles.map((it: Role | string) => ({
				value: it,
				label: typeof it === 'string' ? it : it.name,
			}));
		}
	}

	async onRoleDropdownSelected(selection: Role | string): Promise<void> {
		switch (typeof selection) {
			case 'string':
				await this.onAddCustomRoleSelected();
				break;
			default:
				await this.onRoleSelected(selection as Role);
		}
	}

	async onAddCustomRoleSelected(): Promise<void> {
		this.systemCapabilityItems = [];
		this.addCustomRoleSelected = true;
		this.customRoleName = '';

		this.selectionChangedEvents.emit(undefined);
		this.systemCapabilityTableMode = Mode.create;

		this.systemCapabilityItems = await this.apiService
			.getRoleCapabilities()
			.then((systemCapabilities) =>
				systemCapabilities.map((systemCapability): SystemCapabilityItem => ({
					systemCapability,
				}))
			);
	}

	async onRoleSelected(selectedRole: Role): Promise<void> {
		this.selectedRole = selectedRole;
		this.systemCapabilityItems = [];
		this.addCustomRoleSelected = false;
		this.customRoleName = '';

		this.selectionChangedEvents.emit({
			company: this.selectedCompany,
			role: this.selectedRole,
		});

		const roleId: string = selectedRole.id;
		if (roleId.length !== 0) {
			this.systemCapabilityItems =
				await this.apiService.getSystemCapabilitiesByRoleId(roleId);
		}
		this.systemCapabilityTableMode = Mode.viewOnly;
	}

	onCapabilityToggle(event: any): void {
		const { systemCapability, granted } = event;

		const item = this.systemCapabilityItems.find(
			(it) => it.systemCapability.id === systemCapability.id
		);
		if (item !== undefined) {
			item.granted = granted;
			this.selectionChangedEvents.emit(
				addCustomRoleEvent(
					this.selectedCompany,
					this.customRoleName,
					this.systemCapabilityItems
				)
			);
		}
	}

	onCustomRoleNameChange(value: string): void {
		this.customRoleName = value;
		this.selectionChangedEvents.emit(
			addCustomRoleEvent(
				this.selectedCompany,
				this.customRoleName,
				this.systemCapabilityItems
			)
		);
	}

	getScService() {
		return this.scService;
	}

	getApiService() {
		return this.apiService;
	}
}

const addCustomRoleOption = '+ Add Custom Role';

export interface SelectedCompanyRoleEvent {
	company?: Company;
	role?: Role;
	newRoleInformation?: {
		name: string;
		systemCapabilityItems: SystemCapabilityItem[];
	};
}

const addCustomRoleEvent = (
	company: Company | undefined,
	name: string,
	systemCapabilityItems: SystemCapabilityItem[]
): SelectedCompanyRoleEvent => ({
	company,
	newRoleInformation: {
		name,
		systemCapabilityItems: systemCapabilityItems.filter(
			(it) => it.granted !== undefined
		),
	},
});
